import React from "react";
import { Box, Typography, Button, Container } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import Image89 from "@assets/image89.svg";
import Uni_BgImage from "@assets/Uni_BgImage.png";
import Vector from "@icons/Vector";
import UniComply from "@icons/UniComply";
import AICPA_Image from "@assets/AICPA_Image.png";
import OSHA from "@assets/OSHA.png";
import {
  BODYSUBTITLE,
  BODYTITLE,
  BODYTITLE1,
  GET_STARTED,
} from "@constants/unicomply-keys";

const HeroSection = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  minHeight: 873,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundImage: `url(${Uni_BgImage})`,
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
  backgroundPosition: "center",
  [theme.breakpoints.down("md")]: {
    minHeight: 600,
    paddingTop: 40,
    paddingBottom: 36,
  },
  [theme.breakpoints.down("sm")]: {
    minHeight: 500,
    paddingTop: 180,
    paddingBottom: 40,
  },
}));

const BackgroundOverlay = styled(Box)({
  position: "absolute",
  inset: 0,
  background: "#35D7E4",
  mixBlendMode: "soft-light",
});

const DecorativePattern = styled(Box)(({ theme }) => ({
  position: "absolute",
  left: "50%",
  top: "50%",
  transform: "translate(-50%, -50%)",
  width: 1078,
  height: 433,
  opacity: 0.6,
  [theme.breakpoints.down("md")]: {
    width: 800,
    height: 320,
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    height: 200,
    maxWidth: 400,
  },
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background:
      "radial-gradient(ellipse at center, rgba(255,255,255,0.1) 0%, transparent 70%)",
    borderRadius: "50%",
  },
}));

const LogoIcon = styled(Box)(({ theme }) => ({
  width: 168,
  height: 162,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 60,
  fontWeight: "bold",
  marginBottom: 24,
  [theme.breakpoints.down("md")]: {
    width: 120,
    height: 115,
    marginBottom: 20,
  },
  [theme.breakpoints.down("sm")]: {
    width: 100,
    height: 96,
    marginBottom: 16,
  },
}));

const HeroButton = styled(Button)(({ theme }) => ({
  display: "flex",
  width: "224px",
  height: "60px",
  padding: "12px 24px",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  borderRadius: "60px",
  border: "1px solid #FFF",
  background: "linear-gradient(90deg, #0A829D 0%, #35D7E4 100%)",
  boxShadow: "0 4px 7.3px rgba(133, 233, 255, 0.4)",
  cursor: "pointer",
  textTransform: "none", // prevents MUI Button from making text uppercase
  color: "var(--Text-and-Icon-Inverse, #FFF)",
  fontSize: "18px",
  fontStyle: "normal",
  lineHeight: "22.001px", // 122.228%
  letterSpacing: "-0.4px",
  [theme.breakpoints.down("sm")]: {
    width: "200px",
    height: "50px",
    fontSize: "16px",
    padding: "10px 20px",
  },
}));

const IconCircle = styled(Box)(({ theme }) => ({
  display: "flex",
  width: "60px",
  height: "60px",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  aspectRatio: "1 / 1",
  borderRadius: "60px", // makes it circular
  border: "1px solid #FFF",
  background:
    "radial-gradient(50% 50% at 50% 50%, #F7FEFF 85.58%, rgba(53, 215, 228, 0.50) 100%)",
  boxShadow: "0 4px 7.3px 0 rgba(133, 233, 255, 0.40)",
  cursor: "pointer",
  [theme.breakpoints.down("sm")]: {
    width: "50px",
    height: "50px",
  },
}));

const FloatingElement = styled(Box)<{ delay: number; size: number }>(
  ({ delay, size }) => ({
    position: "absolute",
    width: size,
    height: size,
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    borderRadius: "16px",
    animation: `float 6s ease-in-out infinite ${delay}s`,
  })
);

export default function UniComply_MainBody() {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate("/signup");
  };

  return (
    <HeroSection>
      {/* Background Elements */}
      <BackgroundOverlay />
      <DecorativePattern />

      {/* Floating Elements */}
      <FloatingElement
        delay={0}
        size={80}
        sx={{
          top: { xs: "70%", md: "60%" },
          left: { xs: "5%", md: "10%" },
          width: { xs: 60, md: 80 },
          height: { xs: 60, md: 80 },
          backgroundImage: `url(${Image89})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "contain",
          backgroundPosition: "center",
          backgroundColor: "#fff",
          "@keyframes float": {
            "0%, 100%": { transform: "translateY(0px) rotate(15deg)" },
            "50%": { transform: "translateY(-20px) rotate(15deg)" },
          },
        }}
      />
      <FloatingElement
        delay={1}
        size={80}
        sx={{
          top: { xs: "75%", md: "70%" },
          right: { xs: "5%", md: "15%" },
          width: { xs: 60, md: 80 },
          height: { xs: 60, md: 80 },
          backgroundImage: `url(${AICPA_Image})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "contain",
          backgroundPosition: "center",
          backgroundColor: "#fff",
          flex: "1 0 0",
          "@keyframes float": {
            "0%, 100%": { transform: "translateY(0px) rotate(5deg)" },
            "50%": { transform: "translateY(-20px) rotate(5deg)" },
          },
        }}
      ></FloatingElement>
      <FloatingElement
        delay={2}
        size={70}
        sx={{
          bottom: { xs: "5%", md: "3%" },
          left: { xs: "25%", md: "30%" },
          width: { xs: 50, md: 70 },
          height: { xs: 50, md: 70 },
          backgroundImage: `url(${OSHA})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "contain",
          backgroundPosition: "center",
          backgroundColor: "#fff",
          flex: "1 0 0",
          "@keyframes float": {
            "0%, 100%": { transform: "translateY(0px) rotate(5deg)" },
            "50%": { transform: "translateY(-20px) rotate(5deg)" },
          },
        }}
      >
        {/* <BodyLogo3 /> */}
      </FloatingElement>

      {/* Main Content */}
      <Container
        maxWidth="md"
        sx={{ position: "relative", zIndex: 10, textAlign: "center" }}
      >
        {/* Logo Icon */}
        <LogoIcon sx={{ margin: "0 auto 4px" }}>
          <UniComply />
        </LogoIcon>

        {/* Main Heading */}
        <Typography
          sx={{
            fontSize: { xs: 32, sm: 40, md: 48 },
            fontWeight: 600,
            lineHeight: 1.12,
            marginBottom: 1.5,
            textShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
          }}
        >
          {BODYTITLE}
          <br />
          {BODYTITLE1}
        </Typography>

        {/* Subtitle */}
        <Typography
          sx={{
            fontSize: { xs: 16, sm: 18, md: 20 },
            fontWeight: 400,
            lineHeight: 1.5,
            color: "#065B70",
            marginBottom: 5,
            maxWidth: 600,
            margin: "0 auto 40px",
          }}
        >
          {BODYSUBTITLE}
        </Typography>

        {/* Action Buttons */}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            justifyContent: "center",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "center",
          }}
        >
          <HeroButton onClick={handleGetStarted}>{GET_STARTED}</HeroButton>
          <IconCircle onClick={handleGetStarted}>
            <Vector />
          </IconCircle>
        </Box>
      </Container>
    </HeroSection>
  );
}
